'use client';

import { c as _c } from "react/compiler-runtime";
import type { AutosizeTextAreaRef } from '@/components/textarea/types';
import type { ReviewTypeValue } from '@/components/top-navigation/review-dropdown/types';
import type { PendingComments } from '@/data/v2/pr/pending-comments-types';
import type { PrV2SummaryResponse } from '@/lib/pr-v2/types';
import { ChevronDown, Circle } from 'lucide-react';
import pluralize from 'pluralize';
import { useEffect, useId, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { toast } from 'sonner';
import { PR_V2_REVIEW_OPTIONS } from './pr-v2-review-options';
import { AutosizeTextarea } from '@/components/textarea/autosize-textarea';
import { RadioOption } from '@/components/top-navigation/review-dropdown/radio-option';
import { Button } from '@/components/ui/button';
import { KeyboardShortcut } from '@/components/ui/keyboard-shortcut';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RadioGroup } from '@/components/ui/radio-group';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { usePrV2ReviewSubmit } from '@/data/v2/pr/use-pr-v2-review-submit';
import { usePrV2Reviews } from '@/data/v2/pr/use-pr-v2-reviews';
import { PullRequestState } from '@/lib/github/graphql/types';
import { isPullRequestAuthor } from '@/lib/pr-v2/action-helpers';
import { toPendingGithubCommentId } from '@/lib/pr-v2/pending-comment-ids';
import { dispatchPrV2TabHandoff } from '@/lib/pr-v2/tab-handoff';
import { PR_V2_TABS } from '@/lib/pr-v2/tabs';
import { useReviewDropdownStore } from '@/lib/stores/review-dropdown-store';
import { cn, metaChar } from '@/lib/utils';
import { useAuth } from '@/providers/auth-provider';
import { ReviewType } from '@/types/github';
const REVIEW_SHORTCUT_RESET_MS = 2_000;
type PrV2ReviewDropdownProps = {
  owner: string;
  repo: string;
  number: string;
  summary: PrV2SummaryResponse['pullRequest'];
};
export function PrV2ReviewDropdown(t0) {
  const $ = _c(163);
  const {
    owner,
    repo,
    number,
    summary
  } = t0;
  const reviewsQuery = usePrV2Reviews(owner, repo, number);
  const {
    isOpen,
    reviewType,
    openWithApproval,
    openWithComment,
    openWithRequestChanges,
    closeDropdown
  } = useReviewDropdownStore();
  const reviewSubmit = usePrV2ReviewSubmit(owner, repo, number);
  const textareaRef = useRef(null);
  const lastKeyPressed = useRef(null);
  const lastKeyResetTimeoutRef = useRef(null);
  const [showPendingComments, setShowPendingComments] = useState(false);
  const pendingCommentsListId = useId();
  const {
    user
  } = useAuth();
  let t1;
  if ($[0] !== user) {
    t1 = typeof user?.user_metadata?.user_name === "string" && user.user_metadata.user_name.trim() || typeof user?.email === "string" && user.email.trim() || "";
    $[0] = user;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const currentUserLogin = t1;
  let t2;
  if ($[2] !== summary.state) {
    t2 = summary.state.toLowerCase();
    $[2] = summary.state;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = PullRequestState.Closed.toLowerCase();
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const isClosed = t2 === t3;
  const isClosedUnmerged = isClosed && !summary.merged;
  let t4;
  if ($[5] !== summary.author.login || $[6] !== user) {
    t4 = isPullRequestAuthor(user, summary.author.login);
    $[5] = summary.author.login;
    $[6] = user;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const isAuthor = t4;
  const commentOnly = Boolean(summary.merged);
  let t5;
  if ($[8] !== commentOnly || $[9] !== isAuthor || $[10] !== isOpen || $[11] !== reviewType) {
    t5 = () => {
      if (isOpen) {
        return reviewType as ReviewTypeValue;
      }
      return commentOnly ? ReviewType.COMMENT : isAuthor ? ReviewType.COMMENT : ReviewType.APPROVE;
    };
    $[8] = commentOnly;
    $[9] = isAuthor;
    $[10] = isOpen;
    $[11] = reviewType;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const [selectedReviewType, setSelectedReviewType] = useState(t5);
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  const [prevReviewType, setPrevReviewType] = useState(reviewType);
  let t6;
  if ($[13] !== reviewsQuery.data) {
    t6 = reviewsQuery.data ?? [];
    $[13] = reviewsQuery.data;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const reviews = t6;
  let t7;
  if ($[15] !== currentUserLogin || $[16] !== reviews) {
    let t8;
    if ($[18] !== currentUserLogin) {
      t8 = review => review.userLogin === currentUserLogin && review.state === "PENDING";
      $[18] = currentUserLogin;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    t7 = reviews.some(t8);
    $[15] = currentUserLogin;
    $[16] = reviews;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const userHasPendingReviews = t7;
  let t8;
  if ($[20] !== currentUserLogin || $[21] !== reviews) {
    let t9;
    if ($[23] !== currentUserLogin) {
      t9 = review_0 => review_0.userLogin === currentUserLogin && review_0.state !== "PENDING" && Boolean(review_0.submittedAt);
      $[23] = currentUserLogin;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    t8 = reviews.some(t9);
    $[20] = currentUserLogin;
    $[21] = reviews;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const hasSubmittedReview = t8;
  let t10;
  let t9;
  if ($[25] !== isOpen) {
    t9 = () => {
      if (!isOpen) {
        return;
      }
      const timeoutId = setTimeout(() => {
        textareaRef.current?.focus();
      }, 0);
      return () => clearTimeout(timeoutId);
    };
    t10 = [isOpen];
    $[25] = isOpen;
    $[26] = t10;
    $[27] = t9;
  } else {
    t10 = $[26];
    t9 = $[27];
  }
  useEffect(t9, t10);
  if (prevIsOpen !== isOpen || prevReviewType !== reviewType) {
    setPrevIsOpen(isOpen);
    setPrevReviewType(reviewType);
    if (isOpen) {
      setSelectedReviewType(reviewType as ReviewTypeValue);
    }
  }
  let t11;
  let t12;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => () => {
      if (lastKeyResetTimeoutRef.current) {
        clearTimeout(lastKeyResetTimeoutRef.current);
      }
    };
    t12 = [];
    $[28] = t11;
    $[29] = t12;
  } else {
    t11 = $[28];
    t12 = $[29];
  }
  useEffect(t11, t12);
  let t13;
  if ($[30] !== closeDropdown || $[31] !== openWithApproval || $[32] !== openWithComment || $[33] !== openWithRequestChanges || $[34] !== reviewType || $[35] !== selectedReviewType) {
    t13 = newOpen => {
      if (newOpen) {
        setSelectedReviewType(current => current ?? reviewType as ReviewTypeValue);
        if (selectedReviewType === ReviewType.APPROVE) {
          openWithApproval();
          return;
        }
        if (selectedReviewType === ReviewType.REQUEST_CHANGES) {
          openWithRequestChanges();
          return;
        }
        openWithComment();
        return;
      }
      closeDropdown();
    };
    $[30] = closeDropdown;
    $[31] = openWithApproval;
    $[32] = openWithComment;
    $[33] = openWithRequestChanges;
    $[34] = reviewType;
    $[35] = selectedReviewType;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  const handleOpenChange = t13;
  let t14;
  if ($[37] !== closeDropdown) {
    t14 = comment => {
      const commentId = toPendingGithubCommentId(comment.id);
      closeDropdown();
      dispatchPrV2TabHandoff({
        tab: PR_V2_TABS.CHANGES,
        commentId,
        filename: comment.filename
      });
    };
    $[37] = closeDropdown;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  const handlePendingCommentClick = t14;
  let t15;
  if ($[39] !== commentOnly || $[40] !== isAuthor || $[41] !== reviewSubmit.prPendingComments || $[42] !== reviewSubmit.reviewComment || $[43] !== selectedReviewType || $[44] !== userHasPendingReviews) {
    t15 = () => {
      if (commentOnly && selectedReviewType !== ReviewType.COMMENT) {
        return {
          allowed: false,
          reason: "Merged pull requests only accept comments"
        };
      }
      if (isAuthor && (selectedReviewType === ReviewType.APPROVE || selectedReviewType === ReviewType.REQUEST_CHANGES)) {
        return {
          allowed: false,
          reason: "You cannot approve or request changes on your own pull request"
        };
      }
      if (userHasPendingReviews) {
        return {
          allowed: false,
          reason: "You already have a pending review for this pull request. Please dismiss it in GitHub."
        };
      }
      if (selectedReviewType !== ReviewType.APPROVE && !reviewSubmit.reviewComment.trim() && reviewSubmit.prPendingComments.length === 0) {
        return {
          allowed: false,
          reason: "You must provide a comment or have pending comments"
        };
      }
      return {
        allowed: true,
        reason: null
      };
    };
    $[39] = commentOnly;
    $[40] = isAuthor;
    $[41] = reviewSubmit.prPendingComments;
    $[42] = reviewSubmit.reviewComment;
    $[43] = selectedReviewType;
    $[44] = userHasPendingReviews;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  const canSubmitReview = t15;
  let t16;
  if ($[46] !== canSubmitReview || $[47] !== closeDropdown || $[48] !== reviewSubmit || $[49] !== selectedReviewType) {
    t16 = event => {
      if (event) {
        event.preventDefault();
      }
      const submitStatus = canSubmitReview();
      if (!submitStatus.allowed) {
        return;
      }
      closeDropdown();
      reviewSubmit.submitReviewAsync(reviewSubmit.reviewComment, selectedReviewType).catch(_temp);
    };
    $[46] = canSubmitReview;
    $[47] = closeDropdown;
    $[48] = reviewSubmit;
    $[49] = selectedReviewType;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  const handleSubmit = t16;
  let t17;
  if ($[51] !== canSubmitReview || $[52] !== handleSubmit || $[53] !== isOpen) {
    t17 = event_0 => {
      event_0.preventDefault();
      if (isOpen && canSubmitReview().allowed) {
        handleSubmit();
      }
    };
    $[51] = canSubmitReview;
    $[52] = handleSubmit;
    $[53] = isOpen;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  let t18;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = ["TEXTAREA"];
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  const t19 = !isClosedUnmerged;
  let t20;
  if ($[56] !== t19) {
    t20 = {
      enableOnFormTags: t18,
      enabled: t19
    };
    $[56] = t19;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  let t21;
  if ($[58] !== commentOnly || $[59] !== isAuthor || $[60] !== isClosedUnmerged || $[61] !== isOpen || $[62] !== reviewSubmit.prPendingComments || $[63] !== reviewSubmit.reviewComment || $[64] !== selectedReviewType || $[65] !== userHasPendingReviews) {
    t21 = [isClosedUnmerged, isOpen, reviewSubmit.reviewComment, selectedReviewType, reviewSubmit.prPendingComments, userHasPendingReviews, commentOnly, isAuthor];
    $[58] = commentOnly;
    $[59] = isAuthor;
    $[60] = isClosedUnmerged;
    $[61] = isOpen;
    $[62] = reviewSubmit.prPendingComments;
    $[63] = reviewSubmit.reviewComment;
    $[64] = selectedReviewType;
    $[65] = userHasPendingReviews;
    $[66] = t21;
  } else {
    t21 = $[66];
  }
  useHotkeys("mod+enter", t17, t20, t21);
  let t22;
  if ($[67] !== commentOnly || $[68] !== isAuthor || $[69] !== openWithApproval) {
    t22 = event_1 => {
      event_1.preventDefault();
      if (commentOnly) {
        toast.error("Merged pull requests only accept comments");
        return;
      }
      if (isAuthor) {
        toast.error("You can't approve your own pull request");
        return;
      }
      setSelectedReviewType(ReviewType.APPROVE);
      openWithApproval();
    };
    $[67] = commentOnly;
    $[68] = isAuthor;
    $[69] = openWithApproval;
    $[70] = t22;
  } else {
    t22 = $[70];
  }
  const t23 = !isClosedUnmerged;
  let t24;
  if ($[71] !== t23) {
    t24 = {
      enableOnFormTags: false,
      enabled: t23
    };
    $[71] = t23;
    $[72] = t24;
  } else {
    t24 = $[72];
  }
  let t25;
  if ($[73] !== commentOnly || $[74] !== isAuthor || $[75] !== isClosedUnmerged || $[76] !== openWithApproval) {
    t25 = [isClosedUnmerged, commentOnly, isAuthor, openWithApproval];
    $[73] = commentOnly;
    $[74] = isAuthor;
    $[75] = isClosedUnmerged;
    $[76] = openWithApproval;
    $[77] = t25;
  } else {
    t25 = $[77];
  }
  useHotkeys("shift+a", t22, t24, t25);
  let t26;
  if ($[78] !== openWithComment) {
    t26 = event_2 => {
      event_2.preventDefault();
      setSelectedReviewType(ReviewType.COMMENT);
      openWithComment();
    };
    $[78] = openWithComment;
    $[79] = t26;
  } else {
    t26 = $[79];
  }
  const t27 = !isClosedUnmerged;
  let t28;
  if ($[80] !== t27) {
    t28 = {
      enableOnFormTags: false,
      enabled: t27
    };
    $[80] = t27;
    $[81] = t28;
  } else {
    t28 = $[81];
  }
  let t29;
  if ($[82] !== isClosedUnmerged || $[83] !== openWithComment) {
    t29 = [isClosedUnmerged, openWithComment];
    $[82] = isClosedUnmerged;
    $[83] = openWithComment;
    $[84] = t29;
  } else {
    t29 = $[84];
  }
  useHotkeys("shift+c", t26, t28, t29);
  let t30;
  if ($[85] !== commentOnly || $[86] !== isAuthor || $[87] !== openWithRequestChanges) {
    t30 = event_3 => {
      event_3.preventDefault();
      if (commentOnly) {
        toast.error("Merged pull requests only accept comments");
        return;
      }
      if (isAuthor) {
        toast.error("You can't request changes on your own pull request");
        return;
      }
      setSelectedReviewType(ReviewType.REQUEST_CHANGES);
      openWithRequestChanges();
    };
    $[85] = commentOnly;
    $[86] = isAuthor;
    $[87] = openWithRequestChanges;
    $[88] = t30;
  } else {
    t30 = $[88];
  }
  const t31 = !isClosedUnmerged;
  let t32;
  if ($[89] !== t31) {
    t32 = {
      enableOnFormTags: false,
      enabled: t31
    };
    $[89] = t31;
    $[90] = t32;
  } else {
    t32 = $[90];
  }
  let t33;
  if ($[91] !== commentOnly || $[92] !== isAuthor || $[93] !== isClosedUnmerged || $[94] !== openWithRequestChanges) {
    t33 = [isClosedUnmerged, commentOnly, isAuthor, openWithRequestChanges];
    $[91] = commentOnly;
    $[92] = isAuthor;
    $[93] = isClosedUnmerged;
    $[94] = openWithRequestChanges;
    $[95] = t33;
  } else {
    t33 = $[95];
  }
  useHotkeys("shift+r", t30, t32, t33);
  let t34;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = () => {
      lastKeyPressed.current = "e";
      if (lastKeyResetTimeoutRef.current) {
        clearTimeout(lastKeyResetTimeoutRef.current);
      }
      lastKeyResetTimeoutRef.current = setTimeout(() => {
        lastKeyPressed.current = null;
        lastKeyResetTimeoutRef.current = null;
      }, REVIEW_SHORTCUT_RESET_MS);
    };
    $[96] = t34;
  } else {
    t34 = $[96];
  }
  const t35 = !isClosedUnmerged;
  let t36;
  if ($[97] !== t35) {
    t36 = {
      enableOnFormTags: false,
      enabled: t35
    };
    $[97] = t35;
    $[98] = t36;
  } else {
    t36 = $[98];
  }
  let t37;
  if ($[99] !== isClosedUnmerged) {
    t37 = [isClosedUnmerged];
    $[99] = isClosedUnmerged;
    $[100] = t37;
  } else {
    t37 = $[100];
  }
  useHotkeys("e", t34, t36, t37);
  let t38;
  if ($[101] !== closeDropdown || $[102] !== isOpen || $[103] !== openWithComment) {
    t38 = event_4 => {
      if (lastKeyPressed.current === "e") {
        return;
      }
      event_4.preventDefault();
      if (isOpen) {
        closeDropdown();
      } else {
        openWithComment();
      }
    };
    $[101] = closeDropdown;
    $[102] = isOpen;
    $[103] = openWithComment;
    $[104] = t38;
  } else {
    t38 = $[104];
  }
  const t39 = !isClosedUnmerged;
  let t40;
  if ($[105] !== t39) {
    t40 = {
      enableOnFormTags: false,
      enabled: t39
    };
    $[105] = t39;
    $[106] = t40;
  } else {
    t40 = $[106];
  }
  let t41;
  if ($[107] !== closeDropdown || $[108] !== isClosedUnmerged || $[109] !== isOpen || $[110] !== openWithComment) {
    t41 = [isClosedUnmerged, isOpen, closeDropdown, openWithComment];
    $[107] = closeDropdown;
    $[108] = isClosedUnmerged;
    $[109] = isOpen;
    $[110] = openWithComment;
    $[111] = t41;
  } else {
    t41 = $[111];
  }
  useHotkeys("r", t38, t40, t41);
  if (isClosedUnmerged) {
    return null;
  }
  let t42;
  if ($[112] !== canSubmitReview) {
    t42 = canSubmitReview();
    $[112] = canSubmitReview;
    $[113] = t42;
  } else {
    t42 = $[113];
  }
  const submitStatus_0 = t42;
  let t43;
  if ($[114] !== hasSubmittedReview || $[115] !== reviewSubmit.prPendingComments.length) {
    t43 = reviewSubmit.prPendingComments.length > 0 ? <>Finish review<div className="relative flex size-5 items-center justify-center"><Circle className="size-5 fill-zinc-300 text-zinc-300" /><span className="absolute text-[11px] font-medium text-zinc-900">{reviewSubmit.prPendingComments.length}</span></div></> : hasSubmittedReview ? "Review again" : "Review";
    $[114] = hasSubmittedReview;
    $[115] = reviewSubmit.prPendingComments.length;
    $[116] = t43;
  } else {
    t43 = $[116];
  }
  let t44;
  if ($[117] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <ChevronDown className="size-3.5 opacity-60" />;
    $[117] = t44;
  } else {
    t44 = $[117];
  }
  let t45;
  if ($[118] !== t43) {
    t45 = <TooltipTrigger asChild={true}><PopoverTrigger asChild={true}><Button variant="default" size="sm" className="relative max-md:h-10 max-md:w-full"><div className="flex items-center gap-2">{t43}{t44}</div></Button></PopoverTrigger></TooltipTrigger>;
    $[118] = t43;
    $[119] = t45;
  } else {
    t45 = $[119];
  }
  let t46;
  if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = <TooltipContent><div className="flex items-center gap-2">Review PR<KeyboardShortcut>R</KeyboardShortcut></div></TooltipContent>;
    $[120] = t46;
  } else {
    t46 = $[120];
  }
  let t47;
  if ($[121] !== t45) {
    t47 = <TooltipProvider><Tooltip>{t45}{t46}</Tooltip></TooltipProvider>;
    $[121] = t45;
    $[122] = t47;
  } else {
    t47 = $[122];
  }
  const t48 = selectedReviewType !== ReviewType.APPROVE && reviewSubmit.prPendingComments.length === 0;
  let t49;
  if ($[123] !== reviewSubmit) {
    t49 = event_5 => reviewSubmit.setReviewComment(event_5.target.value);
    $[123] = reviewSubmit;
    $[124] = t49;
  } else {
    t49 = $[124];
  }
  let t50;
  if ($[125] !== owner || $[126] !== repo || $[127] !== reviewSubmit.reviewComment || $[128] !== t48 || $[129] !== t49) {
    t50 = <AutosizeTextarea ref={textareaRef} name="comment" data-hotkeys-ignore="true" placeholder="Leave a review..." className="w-full rounded-md p-3 pb-9 text-base" required={t48} value={reviewSubmit.reviewComment} onChange={t49} minHeight={100} showSendButton={false} owner={owner} repo={repo} enableUserMentions={true} />;
    $[125] = owner;
    $[126] = repo;
    $[127] = reviewSubmit.reviewComment;
    $[128] = t48;
    $[129] = t49;
    $[130] = t50;
  } else {
    t50 = $[130];
  }
  let t51;
  if ($[131] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = value => setSelectedReviewType(value);
    $[131] = t51;
  } else {
    t51 = $[131];
  }
  let t52;
  if ($[132] !== commentOnly || $[133] !== isAuthor) {
    t52 = PR_V2_REVIEW_OPTIONS.map(option => <RadioOption key={option.type} value={option.type} id={option.type} label={option.label} disabled={option.needsOtherAuthor && (isAuthor || commentOnly)} />);
    $[132] = commentOnly;
    $[133] = isAuthor;
    $[134] = t52;
  } else {
    t52 = $[134];
  }
  let t53;
  if ($[135] !== selectedReviewType || $[136] !== t52) {
    t53 = <RadioGroup name="reviewType" value={selectedReviewType} onValueChange={t51} className="space-y-2">{t52}</RadioGroup>;
    $[135] = selectedReviewType;
    $[136] = t52;
    $[137] = t53;
  } else {
    t53 = $[137];
  }
  const t54 = !submitStatus_0.allowed;
  const t55 = selectedReviewType === ReviewType.APPROVE ? "Approve" : selectedReviewType === ReviewType.REQUEST_CHANGES ? "Request changes" : "Submit comment";
  let t56;
  if ($[138] !== t54 || $[139] !== t55) {
    t56 = <TooltipTrigger asChild={true}><div className="w-full"><Button type="submit" size="default" disabled={t54} className="w-full">{t55}</Button></div></TooltipTrigger>;
    $[138] = t54;
    $[139] = t55;
    $[140] = t56;
  } else {
    t56 = $[140];
  }
  let t57;
  if ($[141] !== submitStatus_0.allowed || $[142] !== submitStatus_0.reason) {
    t57 = !submitStatus_0.allowed ? <TooltipContent>{submitStatus_0.reason}</TooltipContent> : <TooltipContent className="flex items-center">Submit review<KeyboardShortcut className="ml-2">{`${metaChar()}↵`}</KeyboardShortcut></TooltipContent>;
    $[141] = submitStatus_0.allowed;
    $[142] = submitStatus_0.reason;
    $[143] = t57;
  } else {
    t57 = $[143];
  }
  let t58;
  if ($[144] !== t56 || $[145] !== t57) {
    t58 = <div className="flex w-full justify-end"><TooltipProvider delayDuration={100}><Tooltip>{t56}{t57}</Tooltip></TooltipProvider></div>;
    $[144] = t56;
    $[145] = t57;
    $[146] = t58;
  } else {
    t58 = $[146];
  }
  let t59;
  if ($[147] !== handlePendingCommentClick || $[148] !== pendingCommentsListId || $[149] !== reviewSubmit.prPendingComments || $[150] !== showPendingComments) {
    t59 = reviewSubmit.prPendingComments.length > 0 ? <div className="w-full"><button type="button" aria-expanded={showPendingComments} aria-controls={pendingCommentsListId} className="flex w-full cursor-pointer items-center justify-between text-sm text-zinc-400 hover:text-zinc-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={() => setShowPendingComments(_temp2)}><span>{`${reviewSubmit.prPendingComments.length} ${pluralize("comment", reviewSubmit.prPendingComments.length)} pending`}</span><ChevronDown className={cn("h-4 w-4 transition-transform", showPendingComments && "scale-y-[-1]")} /></button>{showPendingComments ? <div id={pendingCommentsListId} className="space-y-2 py-2">{reviewSubmit.prPendingComments.map(comment_0 => <button type="button" key={comment_0.id} className="w-full cursor-pointer rounded-md border border-zinc-800 bg-zinc-900 p-2 text-left transition-colors hover:border-zinc-700 hover:bg-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={() => handlePendingCommentClick(comment_0)}><span className="block line-clamp-1 text-sm text-zinc-300">{comment_0.content}</span><span className="mt-1 block text-xs text-zinc-500">{comment_0.filename}</span></button>)}</div> : null}</div> : null;
    $[147] = handlePendingCommentClick;
    $[148] = pendingCommentsListId;
    $[149] = reviewSubmit.prPendingComments;
    $[150] = showPendingComments;
    $[151] = t59;
  } else {
    t59 = $[151];
  }
  let t60;
  if ($[152] !== handleSubmit || $[153] !== t50 || $[154] !== t53 || $[155] !== t58 || $[156] !== t59) {
    t60 = <PopoverContent align="end" collisionPadding={8} className="max-h-[var(--radix-popover-content-available-height)] w-[calc(100vw-1rem)] overflow-y-auto rounded-lg border border-border p-5 md:w-[500px]"><form onSubmit={handleSubmit} className="flex w-full flex-col items-start justify-end gap-5">{t50}{t53}{t58}{t59}</form></PopoverContent>;
    $[152] = handleSubmit;
    $[153] = t50;
    $[154] = t53;
    $[155] = t58;
    $[156] = t59;
    $[157] = t60;
  } else {
    t60 = $[157];
  }
  let t61;
  if ($[158] !== handleOpenChange || $[159] !== isOpen || $[160] !== t47 || $[161] !== t60) {
    t61 = <div className="flex justify-end max-md:flex-1"><Popover open={isOpen} onOpenChange={handleOpenChange}>{t47}{t60}</Popover></div>;
    $[158] = handleOpenChange;
    $[159] = isOpen;
    $[160] = t47;
    $[161] = t60;
    $[162] = t61;
  } else {
    t61 = $[162];
  }
  return t61;
}
function _temp2(current_0) {
  return !current_0;
}
function _temp() {}